import React from 'react'
import uuid from 'uuid/v1'
import { useDropzone } from 'react-dropzone'
import { FormattedMessage, useIntl } from 'react-intl'
import { createNotification } from 'factories/notificationFactory'
import { makeStyles } from '@material-ui/core/styles'
import Avatar from '@material-ui/core/Avatar'
import Tooltip from '@material-ui/core/Tooltip'
import { UserModel } from 'data/models'
import { useDownloadServices, useUserServices } from 'data/services'
import { useIsMounted } from 'utils/utilities'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ConfirmationDialog from 'containers/dialogs/ConfirmationDialog'

const useStyles = makeStyles((theme) => ({
  avatar: (props: { size: number; swapColours: boolean }) => ({
    width: props.size,
    height: props.size,
    margin: 0,
    textTransform: 'uppercase',
    color: props.swapColours ? theme.palette.primary.main : theme.palette.primary.contrastText,
    background: props.swapColours ? theme.palette.primary.contrastText : theme.palette.primary.main,
    '&:hover': {
      background: theme.palette.primary.light,
    },
  }),
}))

interface Props {
  username?: string
  photoUrl?: string
  editing?: boolean
  photoUploaded?: (name?: string) => void
  size?: number
  swapColours?: boolean
}

// Limit concurrent photo downloads to prevent Lambda throttling when many avatars mount at once
const MAX_CONCURRENT_PHOTOS = 3;
let _photoRunning = 0;
const _photoPending: Array<() => void> = [];

function _drainPhotoQueue() {
  while (_photoRunning < MAX_CONCURRENT_PHOTOS && _photoPending.length > 0) {
    _photoRunning++;
    const task = _photoPending.shift()!;
    task();
  }
}

function enqueuePhotoLoad(load: () => Promise<void>) {
  const run = () => {
    load().finally(() => {
      _photoRunning--;
      _drainPhotoQueue();
    });
  };
  if (_photoRunning < MAX_CONCURRENT_PHOTOS) {
    _photoRunning++;
    run();
  } else {
    _photoPending.push(run);
  }
}

const UserAvatar: React.FC<Props> = ({ photoUrl, username, editing = false, photoUploaded = undefined, size = 40, swapColours = false }) => {
  const classes = useStyles({ size, swapColours })
  const intl = useIntl()
  const [imageUrl, setImageUrl] = React.useState<string | undefined>(undefined)
  const [avatarMenuAnchor, setAvatarMenuAnchor] = React.useState<null | HTMLElement>(null)
  const [showConfirm, setShowConfirm] = React.useState(false) // State for Dialog

  const downloadServices = useDownloadServices()
  const userServices = useUserServices()
  const isMounted = useIsMounted()

  React.useEffect(() => {
    // Skip the download when there is no real photo. Backend hands out a
    // `profile_picture_dummy.png` placeholder for users that never set an
    // avatar — without this guard UserAvatar fires a GetUserFile request
    // (often with undefined company/user ids before the session is fully
    // loaded) that always 404s and pollutes the network panel.
    if (!photoUrl || photoUrl.toLowerCase().includes('profile_picture_dummy')) {
      setImageUrl(undefined);
      return;
    }
    let cancelled = false;
    enqueuePhotoLoad(async () => {
      if (cancelled || !isMounted.current) return;
      try {
        const blob = await downloadServices.getUserFile(photoUrl);
        const url = (window.URL || window.webkitURL).createObjectURL(blob);
        if (!cancelled && isMounted.current) setImageUrl(url);
      } catch {
        // silently fail — avatar falls back to initials
      }
    });
    return () => { cancelled = true; };
  }, [photoUrl])

  const handleContextMenu = (event: React.MouseEvent) => {
    if (editing && photoUrl) {
      event.preventDefault()
      setAvatarMenuAnchor(event.currentTarget as HTMLElement)
    }
  }

  // Triggered from Menu Item
  const handleRemoveClick = () => {
    setAvatarMenuAnchor(null)
    setShowConfirm(true) // Open Dialog instead of deleting immediately
  }

  const executeRemove = () => {
    setShowConfirm(false)
    setImageUrl(undefined)
    if (photoUploaded) {
      photoUploaded(undefined);
      createNotification.success('Profile picture has been removed successfully');
      window.dispatchEvent(new CustomEvent('USER_PHOTO_UPDATED', { detail: { newUrl: undefined } }));
    }
  }

  const { acceptedFiles, getRootProps, open, getInputProps } = useDropzone({
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: 'image/*',
    onDropAccepted: (files, e) => handleUpload(files[0]),
  })

  const getAvatarLetter = (user?: UserModel) => {
    const safeName = (username || '').trim()
    return (safeName[0] || 'U').toUpperCase()
  }

const handleUpload = async (file: File) => {
  const localPreviewUrl = (window.URL || window.webkitURL).createObjectURL(file);
  setImageUrl(localPreviewUrl);

  acceptedFiles.length = 0;
  acceptedFiles.splice(0, acceptedFiles.length);

  if (photoUploaded) {
    // Generate a unique filename per upload — reusing the original file.name risks
    // colliding with a prior upload of the same name, which can make a fresh photo
    // look like it "didn't update" since it resolves to the same storage key/URL.
    const extension = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : ''
    const uniqueFileName = `${uuid()}${extension}`
    try {
      const formData = new FormData();
      formData.append('file', file, uniqueFileName);
      await userServices.uploadPhoto(formData, uniqueFileName);
      photoUploaded(uniqueFileName);
      window.dispatchEvent(new CustomEvent('USER_PHOTO_UPDATED', { detail: { newUrl: uniqueFileName } }));
    } catch {
      setImageUrl(undefined);
      createNotification.error(intl.formatMessage({ id: 'user.avatar.upload.error', defaultMessage: 'Failed to upload photo. Please try again.' }));
    }
  }
};

  if (editing)
    return (
      <div {...getRootProps({ className: 'dropzone' })} onContextMenu={handleContextMenu}>
        <input {...getInputProps()} />
        <Tooltip
          title={
            photoUrl
              ? intl.formatMessage({
                  id: 'user.avatar.tooltip.rightClickRemove',
                  defaultMessage: 'Right-click to remove',
                })
              : intl.formatMessage({
                  id: 'user.avatar.tooltip.dropPhoto',
                  defaultMessage: 'Click or drop a profile photo here',
                })
          }
        >
          <Avatar variant="circle" onClick={() => open()} className={classes.avatar} style={{ cursor: 'pointer' }} src={imageUrl}>
            {getAvatarLetter()}
          </Avatar>
        </Tooltip>

        <Menu anchorEl={avatarMenuAnchor} open={Boolean(avatarMenuAnchor)} onClose={() => setAvatarMenuAnchor(null)}>
          <MenuItem onClick={handleRemoveClick} style={{ color: 'red' }}>
            <FormattedMessage id="user.avatar.menu.removeImage" defaultMessage="Remove photo" />
          </MenuItem>
        </Menu>

        <ConfirmationDialog
          open={showConfirm}
          message={intl.formatMessage({
            id: 'delete-photo-confirmation',
            defaultMessage: 'Are you sure you want to remove the profile picture?',
          })}
          handleOk={executeRemove}
          handleCancel={() => setShowConfirm(false)}
        />
      </div>
    )

  return (
    <Avatar variant="circle" className={classes.avatar} src={imageUrl}>
      {getAvatarLetter()}
    </Avatar>
  )
}

export default UserAvatar
